import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useResultModal } from '@/hooks/useResultModal';
import { isAppleSignInAvailable, startAppleSignIn } from '@/native/appleSignIn';
import { designSystem } from '@/styles/theme';
import ButtonSpinner from '@/components/common/elements/ButtonSpinner';

interface SignupTypeSelectProps {
    /** 유형 선택 시 호출 — 다음 스텝(약관 동의)으로 진행 */
    onSelect: (type: 'user' | 'clinic') => void;
    /** 가입·로그인 뒤 돌아갈 화면(없으면 홈) — 소셜 왕복 뒤에도 쓰도록 남겨 둔다 */
    returnUrl?: string | null;
}

const FormContainer = styled.div`
    width: 100%;
`;

const Guide = styled.p`
    font-size: 15px;
    color: #4b5563;
    text-align: center;
    margin: 4px 0 28px;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;

    @media (max-width: 768px) {
        gap: 10px;
    }
`;

/** 버튼 + 그 아래 가입 유형 설명 한 묶음(2026-10-10 — 공고를 올리려는 실장님이 일반회원으로 가입하던 문의 대응) */
const TypeColumn = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const TypeNote = styled.p`
    font-size: 12px;
    line-height: 1.45;
    color: #9ca3af;
    text-align: center;
    margin: 0;
    word-break: keep-all;
`;

const SelectButton = styled.button`
    flex: none;
    width: 100%;
    height: 56px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    background: ${designSystem.colors.primary};
    color: #fff;
    transition: all 0.2s;

    &:hover {
        background: ${designSystem.colors.primaryDark};
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(90, 155, 150, 0.3);
    }

    @media (max-width: 768px) {
        height: 52px;
        font-size: 15px;
    }
`;

const OrDivider = styled.div`
    display: flex;
    align-items: center;
    text-align: center;
    color: #9ca3af;
    font-size: 13px;
    margin: 20px 0;

    &::before, &::after {
        content: '';
        flex: 1;
        border-bottom: 1px solid #e5e7eb;
    }
    span { padding: 0 12px; }
`;

const KakaoStartButton = styled.button`
    width: 100%;
    height: 56px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    background: #FEE500;
    color: #191600;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;

    &:hover { background: #f5dc00; }
    svg { width: 20px; height: 20px; flex-shrink: 0; display: block; }
`;

// Apple HIG: 카카오 버튼과 같은 높이·모서리, 검은 배경 + 흰 로고.
const AppleStartButton = styled.button`
    width: 100%;
    height: 56px;
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    background: #000;
    color: #fff;
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;

    &:hover:not(:disabled) { background: #1a1a1a; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
    svg { width: 20px; height: 20px; flex-shrink: 0; display: block; }
`;

const SubNote = styled.p`
    font-size: 12px;
    color: #9ca3af;
    text-align: center;
    margin: 12px 0 0;
`;

const SignupTypeSelect = ({ onSelect, returnUrl = null }: SignupTypeSelectProps) => {
    const { showErrorMessage, resultModal } = useResultModal();
    const navigate = useNavigate();
    // Apple 로그인 진행 중(웹 리다이렉트 준비 / iOS 네이티브 시트) — 중복 클릭 방지
    const [appleLoading, setAppleLoading] = useState(false);

    const startKakao = () => {
        const clientId = import.meta.env.VITE_KAKAO_REST_API_KEY;
        if (!clientId) {
            showErrorMessage('카카오 로그인 설정이 필요합니다.');
            return;
        }
        const redirectUri = encodeURIComponent(
            import.meta.env.VITE_KAKAO_OAUTH_CALLBACK_URL || `${window.location.origin}/login/callback/kakao`
        );
        const state = crypto.randomUUID();
        sessionStorage.setItem('kakao_oauth_state', state);
        // OAuth 왕복 뒤 돌아갈 화면(기존 회원이면 콜백이 바로, 신규면 가입을 마친 뒤 로그인 화면이 쓴다)
        if (returnUrl) sessionStorage.setItem('login_return_url', returnUrl);
        else sessionStorage.removeItem('login_return_url');
        const channelPublicId = import.meta.env.VITE_KAKAO_CHANNEL_PUBLIC_ID || '_BAjPX';
        const channelParam = channelPublicId ? `&channel_public_id=${channelPublicId}` : '';
        window.location.href = `https://kauth.kakao.com/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}${channelParam}`;
    };

    // Apple 로 시작하기 — 웹은 Apple 페이지로 이동, iOS 앱은 네이티브 시트 결과를 콜백 화면으로 넘긴다.
    const startApple = async () => {
        try {
            setAppleLoading(true);
            const result = await startAppleSignIn(returnUrl ?? undefined);
            if (result.status === 'native') {
                navigate('/login/callback/apple', { state: { appleLogin: result.payload } });
            }
            // 'cancelled' 는 사용자가 직접 닫은 것 → 아무 안내도 띄우지 않는다
        } catch (error) {
            console.error('Apple login error:', error);
            showErrorMessage('Apple 로그인을 시작할 수 없습니다. 잠시 후 다시 시도해주세요.');
        } finally {
            setAppleLoading(false);
        }
    };

    return (
        <FormContainer>
            {resultModal}
            <Guide>가입 유형을 선택해주세요</Guide>

            <ButtonGroup>
                <TypeColumn>
                    <SelectButton type="button" onClick={() => onSelect('user')}>
                        일반회원 가입
                    </SelectButton>
                    <TypeNote>위생사·스탭·원장님 개인<br />구직·속닥속닥·세미나</TypeNote>
                </TypeColumn>
                <TypeColumn>
                    <SelectButton type="button" onClick={() => onSelect('clinic')}>
                        병/의원 가입
                    </SelectButton>
                    <TypeNote>채용 공고를 올리는 치과 계정<br />치과 정보 확인 후 이용</TypeNote>
                </TypeColumn>
            </ButtonGroup>

            {(
                <>
                    <OrDivider><span>또는</span></OrDivider>

                    <KakaoStartButton type="button" onClick={startKakao}>
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 4C7.03 4 3 7.13 3 11c0 2.5 1.69 4.7 4.23 5.95-.18.64-.67 2.37-.77 2.74-.12.46.17.45.36.33.15-.1 2.36-1.6 3.32-2.25.6.09 1.22.13 1.86.13 4.97 0 9-3.13 9-7C21 7.13 16.97 4 12 4z"/>
                        </svg>
                        카카오로 시작하기
                    </KakaoStartButton>
                    {/* 구버전 iOS 앱(네이티브 플러그인 없음)에서는 Apple 버튼 숨김 — 앱 업데이트 후 노출 */}
                    {isAppleSignInAvailable() && (
                    <AppleStartButton type="button" onClick={startApple} disabled={appleLoading}>
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                        </svg>
                        {appleLoading ? <ButtonSpinner /> : 'Apple로 시작하기'}
                    </AppleStartButton>
                    )}
                    <SubNote>{isAppleSignInAvailable() ? '카카오·Apple 로 시작하면 일반회원으로 가입됩니다.' : '카카오로 시작하면 일반회원으로 가입됩니다.'}</SubNote>
                </>
            )}
        </FormContainer>
    );
};

export default SignupTypeSelect;
